/*
 * Copyright 2026 Sony Corporation
 */

import type { Transform } from './types';

/**
 * Pan and tilt as a view onto the stored Euler rotation, not new fields
 * (camera-simulation.md §5.4).
 *
 * Babylon's triple is (pitch, yaw, roll) with a positive pitch looking down, and
 * a tilt conventionally looks up when positive, hence the sign. Degrees, because
 * radians are not a number anyone reads.
 */
const DEGREES = 180 / Math.PI;

/** Into (-180, 180], so a pan that went round twice still reads as a heading. */
const wrap = (degrees: number): number => {
  const wrapped = ((((degrees + 180) % 360) + 360) % 360) - 180;
  return wrapped === -180 ? 180 : wrapped;
};

export interface PanTilt {
  pan: number;
  tilt: number;
  roll: number;
}

export const panTiltOf = ({ rotation }: Transform): PanTilt => ({
  pan: wrap(rotation.y * DEGREES),
  tilt: wrap(-rotation.x * DEGREES),
  roll: wrap(rotation.z * DEGREES),
});

export const withPanTilt = (transform: Transform, change: Partial<PanTilt>): Transform => {
  const current = panTiltOf(transform);
  const next = { ...current, ...change };
  return {
    position: transform.position,
    rotation: { x: -next.tilt / DEGREES, y: next.pan / DEGREES, z: next.roll / DEGREES },
  };
};
